import React, { useEffect, useRef } from 'react';
import '../../../styles/common/custom-alert.css';

interface CustomAlertProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
  autoCloseDelay?: number | null;
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  message,
  onConfirm,
  onCancel,
  confirmText = "OK",
  cancelText = "Cancel",
  showConfirmButton = true,
  showCancelButton = true,
  autoCloseDelay = null
}) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Set up auto-close timer if specified
    if (autoCloseDelay !== null) {
      timerRef.current = setTimeout(() => {
        onConfirm();
      }, autoCloseDelay);
    }
    
    // Clear the timeout when component unmounts
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [autoCloseDelay, onConfirm]);

  // Clear the timer when a button is clicked
  const handleConfirm = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    onConfirm();
  };

  const handleCancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    onCancel();
  };

  return (
    <div className="custom-alert-overlay">
      <div className="custom-alert-box">
        <p>{message}</p>
        <div className="custom-alert-buttons">
          {showConfirmButton && (
            <button 
              className="custom-alert-button confirm-button" 
              onClick={handleConfirm}
            >
              {confirmText}
            </button>
          )}
          {showCancelButton && (
            <button 
              className="custom-alert-button cancel-button" 
              onClick={handleCancel}
            >
              {cancelText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAlert;